import { get } from 'lodash';
import moment from 'moment';

import {
  DATE_FORMAT_FULL_MONTH_YEAR,
  EMPLOYMENT_BASIS,
  EMPLOYMENT_EMPLOYMENT_TYPE,
  EMPLOYMENT_TYPE,
  FIELD_PATHS,
} from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { Company } from '@funnels/types';
import { SummaryStringDisplay } from '@funnels/ui-components';
import { Accordion, FunnelGrid, FunnelGridUnit } from '@funnels/ui-components/Common';
import { getFieldValueLabel } from '@funnels/utils';

interface EmploymentSummaryAccordionProps {
  employmentIndex: number;
  onClickEditButton?: () => void;
}

export const EmploymentSummaryAccordion = ({ employmentIndex, onClickEditButton }: EmploymentSummaryAccordionProps) => {
  const [formValues] = useFormValues();
  const type: EMPLOYMENT_TYPE = get(formValues, FIELD_PATHS.EMPLOYMENT_TYPE(employmentIndex));
  const role = {
    [EMPLOYMENT_TYPE.PRIMARY]: 'Primary role',
    [EMPLOYMENT_TYPE.SECONDARY]: 'Secondary role',
    [EMPLOYMENT_TYPE.PREVIOUS]: 'Previous role',
  }[type];
  const isNotWorking =
    get(formValues, FIELD_PATHS.EMPLOYMENT_EMPLOYMENT_TYPE(employmentIndex)) === EMPLOYMENT_EMPLOYMENT_TYPE.NOT_WORKING;
  const isSelfEmployed =
    get(formValues, FIELD_PATHS.EMPLOYMENT_EMPLOYMENT_TYPE(employmentIndex)) ===
    EMPLOYMENT_EMPLOYMENT_TYPE.SELF_EMPLOYED;
  const jobTitle = get(formValues, FIELD_PATHS.EMPLOYMENT_ROLE_ANZSCO_OCCUPATION(employmentIndex));
  const employmentBasis = getFieldValueLabel(get(formValues, FIELD_PATHS.EMPLOYMENT_EMPLOYMENT_BASIS(employmentIndex)));
  const startDate = moment(get(formValues, FIELD_PATHS.EMPLOYMENT_START_DATE(employmentIndex))).format(
    DATE_FORMAT_FULL_MONTH_YEAR
  );
  const endDate = moment(get(formValues, FIELD_PATHS.EMPLOYMENT_END_DATE(employmentIndex))).format(
    DATE_FORMAT_FULL_MONTH_YEAR
  );
  const returnToWorkDate = moment(
    get(formValues, FIELD_PATHS.EMPLOYMENT_MATERNITY_LEAVE_RETURN_DATE(employmentIndex))
  ).format(DATE_FORMAT_FULL_MONTH_YEAR);
  const returnToWorkBasis = getFieldValueLabel(
    get(formValues, FIELD_PATHS.EMPLOYMENT_MATERNITY_LEAVE_RETURN_BASIS(employmentIndex))
  );

  const companies: Company[] = get(formValues, FIELD_PATHS.COMPANIES(), []);
  const companyId = get(formValues, FIELD_PATHS.EMPLOYMENT_COMPANY_ID(employmentIndex));
  const companyIndex = companies.findIndex(
    (company) => get({ companies: [company] }, FIELD_PATHS.COMPANY_ID(0)) === companyId
  );
  const businessName = get(formValues, FIELD_PATHS.COMPANY_BUSINESS_NAME(companyIndex));
  const businessStructure = getFieldValueLabel(get(formValues, FIELD_PATHS.COMPANY_BUSINESS_STRUCTURE(companyIndex)));
  const abn = get(formValues, FIELD_PATHS.COMPANY_ABN(companyIndex));
  const businessAddress = get(formValues, FIELD_PATHS.COMPANY_ADDRESS_FORMATTED(companyIndex));
  const businessPhoneNumber = get(formValues, FIELD_PATHS.COMPANY_PHONE_NUMBER(companyIndex));

  return (
    <Accordion headerText={role} onPrimaryButtonClick={onClickEditButton} primaryButtonLabel="Edit">
      <FunnelGrid>
        {isNotWorking && (
          <>
            <FunnelGridUnit size={1 / 2} my="xs">
              <SummaryStringDisplay title="Employment type" value="Not working" />
            </FunnelGridUnit>
            <FunnelGridUnit size={1 / 2} my="xs">
              <SummaryStringDisplay title="Start date" value={startDate} />
            </FunnelGridUnit>
          </>
        )}
        {!isNotWorking && (
          <>
            <FunnelGridUnit size={1 / 2} my="xs">
              <SummaryStringDisplay title="Job title" value={jobTitle} />
            </FunnelGridUnit>
            <FunnelGridUnit size={1 / 2} my="xs">
              <SummaryStringDisplay title="Employment basis" value={employmentBasis} />
            </FunnelGridUnit>
            <FunnelGridUnit size={1 / 2} my="xs">
              <SummaryStringDisplay title="Start date" value={startDate} />
            </FunnelGridUnit>
            {employmentBasis === getFieldValueLabel(EMPLOYMENT_BASIS.MATERNITY_LEAVE) && (
              <>
                <FunnelGridUnit size={1 / 2} my="xs">
                  <SummaryStringDisplay title="Return to work date" value={returnToWorkDate} />
                </FunnelGridUnit>
                <FunnelGridUnit size={1 / 2} my="xs">
                  <SummaryStringDisplay title="Return to work basis" value={returnToWorkBasis} />
                </FunnelGridUnit>
              </>
            )}
            {type === EMPLOYMENT_TYPE.PREVIOUS && (
              <FunnelGridUnit size={1 / 2} my="xs">
                <SummaryStringDisplay title="End date" value={endDate} />
              </FunnelGridUnit>
            )}
            <FunnelGridUnit size={1 / 2} my="xs">
              <SummaryStringDisplay title={`${isSelfEmployed ? 'Business' : "Employer's"} name`} value={businessName} />
            </FunnelGridUnit>
            <FunnelGridUnit size={1 / 2} my="xs">
              <SummaryStringDisplay title="ABN" value={abn} />
            </FunnelGridUnit>
            {isSelfEmployed && (
              <FunnelGridUnit size={1 / 2} my="xs">
                <SummaryStringDisplay title="Business structure" value={businessStructure} />
              </FunnelGridUnit>
            )}
            <FunnelGridUnit size={1 / 2} my="xs">
              <SummaryStringDisplay title="Business address" value={businessAddress} />
            </FunnelGridUnit>

            <FunnelGridUnit size={1 / 2} my="xs">
              <SummaryStringDisplay title="Phone number" value={businessPhoneNumber} />
            </FunnelGridUnit>
          </>
        )}
      </FunnelGrid>
    </Accordion>
  );
};
